import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ProductGrid from './components/ProductGrid';
import ProductPage from './pages/ProductPage';
import { Product } from './data/products';
import { fetchProductsByCategory } from './api/productsApi';
import { CatalogStats, StatListKey, fetchCatalogStats } from './api/statsApi';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import CircleIcon from '@mui/icons-material/Circle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import BugReportIcon from '@mui/icons-material/BugReport';
import PetsIcon from '@mui/icons-material/Pets';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import YardIcon from '@mui/icons-material/Yard';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { motion } from 'framer-motion';
import InsectosAranasImg from './assets/Images/Categorias/CatInsectosAranas.png';
import RoedoresImg from './assets/Images/Categorias/CatRoedores.png';
import UsoHumanoImg from './assets/Images/Categorias/CatUsoHumano.png';
import JardinImg from './assets/Images/Categorias/CatJardin.png';
import MascotasImg from './assets/Images/Categorias/CatMascotas.png';
import LimpiezaImg from './assets/Images/Categorias/CatLimpieza.png';
import AutoHogarImg from './assets/Images/Categorias/CatAutoHogar.png';
import TodosImg from './assets/Images/Categorias/CatTodos.png';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';

const categorias = [
  {
    label: 'Insectos y Arañas',
    value: 'insectos-aranas',
    icon: <BugReportIcon sx={{ fontSize: 48, color: '#388e3c' }} />,
    img: InsectosAranasImg,
  },
  {
    label: 'Roedores',
    value: 'roedores',
    icon: <PetsIcon sx={{ fontSize: 48, color: '#6d4c41' }} />,
    img: RoedoresImg,
  },
  {
    label: 'Jardín',
    value: 'jardin',
    icon: <YardIcon sx={{ fontSize: 48, color: '#2e7d32' }} />,
    img: JardinImg,
  },
  {
    label: 'Mascotas',
    value: 'mascotas',
    icon: <PetsIcon sx={{ fontSize: 48, color: '#f57c00' }} />,
    img: MascotasImg,
  },
  {
    label: 'Uso Humano (Piojos y Talco)',
    value: 'uso-humano',
    icon: <PersonIcon sx={{ fontSize: 48, color: '#1976d2' }} />,
    img: UsoHumanoImg,
  },
  {
    label: 'Limpieza (Desinfección)',
    value: 'limpieza-desinfeccion',
    icon: <CleaningServicesIcon sx={{ fontSize: 48, color: '#0288d1' }} />,
    img: LimpiezaImg,
  },
  {
    label: 'Auto y Hogar',
    value: 'auto-hogar',
    icon: <HomeIcon sx={{ fontSize: 48, color: '#7b1fa2' }} />,
    img: AutoHogarImg,
  },
  {
    label: 'Todos',
    value: 'todos',
    icon: <CategoryIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
    img: TodosImg,
  },
];

const AnimatedNumber = ({ value, duration = 1.2, fontSize, color }: { value: number, duration?: number, fontSize?: number, color?: string }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const startTime = performance.now();
    function animate(now: number) {
      const elapsed = (now - startTime) / 1000;
      if (elapsed < duration) {
        setDisplay(Math.floor(value * (elapsed / duration)));
        requestAnimationFrame(animate);
      } else {
        setDisplay(value);
      }
    }
    requestAnimationFrame(animate);
    // eslint-disable-next-line
  }, [value]);
  return <span style={{ fontSize, color, fontWeight: 'bold' }}>{display.toLocaleString()}</span>;
};

const STAT_COLOR = '#388e3c';

function App() {
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState<string | null>(null);
  const [mostrarProductos, setMostrarProductos] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(true);
  const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [statDetail, setStatDetail] = useState<{ title: string; items: string[] } | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [cart, setCart] = useState<{ product: Product, quantity: number }[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 100) {
        setWizardOpen(false);
      } else if (y < 40) {
        setWizardOpen(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchCatalogStats()
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        if (!cancelled) setStats(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const estadisticas: { label: string; value: number; color: string; listKey: StatListKey }[] = [
    { label: 'Estados Activos', value: stats?.estadosActivos ?? 0, color: STAT_COLOR, listKey: 'estados' },
    { label: 'Productos en Venta', value: stats?.productosEnVenta ?? 0, color: STAT_COLOR, listKey: 'productos' },
    { label: 'Ciudades Activas', value: stats?.ciudadesActivas ?? 0, color: STAT_COLOR, listKey: 'ciudades' },
    { label: 'Sucursales Activas', value: stats?.sucursalesActivas ?? 0, color: STAT_COLOR, listKey: 'sucursales' },
    { label: 'Clientes Activos', value: stats?.clientesActivos ?? 0, color: STAT_COLOR, listKey: 'clientes' },
  ];

  const openStatDetail = (label: string, listKey: StatListKey) => {
    const items = stats?.[listKey] ?? [];
    setStatDetail({ title: label, items });
  };

  useEffect(() => {
    if (!mostrarProductos || !categoriaSeleccionada) {
      return;
    }

    let cancelled = false;
    setProductsLoading(true);
    setProductsError(null);

    fetchProductsByCategory(categoriaSeleccionada)
      .then((items) => {
        if (cancelled) return;
        setCategoryProducts(items);
        if (items.length === 0) {
          setProductsError('No hay productos en esta categoría.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setProductsError('No se pudo conectar con el servidor.');
        setCategoryProducts([]);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mostrarProductos, categoriaSeleccionada]);

  const addToCart = (product: Product, quantity: number = 1) => {
    setCart((prev) => {
      const found = prev.find((item) => item.product.id === product.id);
      if (found) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        return [...prev, { product, quantity }];
      }
    });
    setSnackbarMsg(`${product.name} agregado${quantity > 1 ? ` (${quantity})` : ''} al carrito`);
    setSnackbarOpen(true);
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateCartQuantity = (productId: string, quantity: number) => {
    setCart((prev) => prev.map((item) =>
      item.product.id === productId ? { ...item, quantity } : item
    ));
  };

  const scrollToId = (id: string) => {
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
  };

  const goToInicioCategorias = () => {
    setMostrarProductos(false);
    setCategoriaSeleccionada(null);
    navigate('/');
    setTimeout(() => scrollToId('categorias'), 0);
  };

  const handleNavigate = (section: 'inicio' | 'productos' | 'categorias' | 'contacto') => {
    if (section === 'inicio' || section === 'productos' || section === 'categorias') {
      goToInicioCategorias();
      return;
    }
    if (section === 'contacto') {
      navigate('/');
      setTimeout(() => scrollToId('contacto'), 0);
    }
  };

  const handleSelectCategory = (category: string) => {
    setCategoriaSeleccionada(category);
    setMostrarProductos(true);
    navigate('/');
    setTimeout(() => scrollToId('categorias'), 0);
  };

  const homeContent = (
    <>
      {/* Wizard de bienvenida: colapsa al scrollear o con el botón */}
      <Box
        id="inicio"
        component="section"
        sx={{
          width: '100%',
          bgcolor: '#fafafa',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          px: 2,
          py: wizardOpen ? { xs: 2.5, sm: 3 } : 1,
          transition: 'padding 0.25s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            maxWidth: 1100,
            mx: 'auto',
            mb: wizardOpen ? 1.5 : 0,
          }}
        >
          <Typography
            variant={wizardOpen ? (isMobile ? 'h6' : 'h5') : 'subtitle1'}
            color="#388e3c"
            fontWeight="bold"
            sx={{ textAlign: 'center' }}
          >
            Bienvenidos a Defenders
          </Typography>
          <IconButton
            aria-label={wizardOpen ? 'Colapsar bienvenida' : 'Expandir bienvenida'}
            onClick={() => setWizardOpen((open) => !open)}
            size="small"
            sx={{ color: '#388e3c' }}
          >
            {wizardOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={wizardOpen} timeout={280}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 1100, mx: 'auto' }}>
              <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary" sx={{ mb: 2 }}>
                Estamos para servirle y atenderle.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 1.5 },
                  justifyContent: 'center',
                  alignItems: 'stretch',
                }}
              >
                {estadisticas.map((stat, idx) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 + idx * 0.08 }}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-label={`Ver lista de ${stat.label}`}
                      onClick={() => openStatDetail(stat.label, stat.listKey)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openStatDetail(stat.label, stat.listKey);
                        }
                      }}
                      sx={{
                        textAlign: 'center',
                        px: { xs: 1.25, sm: 1.5 },
                        py: 1,
                        borderRadius: 2,
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        minWidth: { xs: 96, sm: 108 },
                        cursor: 'pointer',
                        transition: 'background-color 0.2s, box-shadow 0.2s, border-color 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(76, 175, 80, 0.18)',
                          borderColor: 'rgba(76, 175, 80, 0.55)',
                          boxShadow: '0 4px 14px rgba(56, 142, 60, 0.18)',
                        },
                        '&:focus-visible': {
                          outline: '2px solid #388e3c',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Typography variant={isMobile ? 'subtitle1' : 'h6'} color={stat.color} fontWeight="bold" lineHeight={1.2}>
                        <AnimatedNumber value={stat.value} fontSize={isMobile ? 16 : 22} color={stat.color} duration={1.2} />
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {stat.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#66bb6a', fontSize: 10, display: 'block', mt: 0.25 }}>
                        Ver lista
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
            </Box>
          </motion.div>
        </Collapse>
      </Box>

      <Box id="categorias" sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', pt: { xs: 3, sm: 4 }, px: 2 }}>
        {!mostrarProductos ? (
          <>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ mb: 3 }}>
              ¿Qué categorías buscas?
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, maxWidth: 1200, width: '100%' }}>
              {categorias.map((cat, index) => (
                <motion.div
                  key={cat.value}
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1,
                    ease: 'easeOut',
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: { duration: 0.2 },
                  }}
                  whileTap={{
                    scale: 0.95,
                    transition: { duration: 0.1 },
                  }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      height: '100%',
                      border: categoriaSeleccionada === cat.value ? '2px solid #388e3c' : '2px solid transparent',
                      boxShadow: categoriaSeleccionada === cat.value ? 6 : 2,
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      borderRadius: 4,
                    }}
                    onClick={() => {
                      setCategoriaSeleccionada(cat.value);
                      setMostrarProductos(true);
                    }}
                    elevation={categoriaSeleccionada === cat.value ? 8 : 2}
                  >
                    <CardActionArea>
                      <CardMedia
                        component="img"
                        height={isMobile ? '120' : '160'}
                        image={cat.img}
                        alt={cat.label}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {cat.icon}
                        <Typography gutterBottom variant="h6" component="div" fontWeight="bold">
                          {cat.label}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </motion.div>
              ))}
            </Box>
            {categoriaSeleccionada && !mostrarProductos && (
              <Typography variant="h6" color="primary" sx={{ mt: 4 }}>
                Seleccionaste: {categorias.find((c) => c.value === categoriaSeleccionada)?.label}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: 1200, mb: 4 }}>
              <Button
                variant="outlined"
                onClick={() => setMostrarProductos(false)}
                sx={{ mb: 2 }}
              >
                ← Volver a Categorías
              </Button>
            </Box>
            {productsLoading && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>Cargando productos...</Typography>
            )}
            {productsError && (
              <Typography color="warning.main" sx={{ mb: 2 }}>{productsError}</Typography>
            )}
            {!productsLoading && (
              <ProductGrid
                products={categoryProducts}
                categoryName={categorias.find((c) => c.value === categoriaSeleccionada)?.label || ''}
                addToCart={addToCart}
              />
            )}
          </>
        )}
      </Box>
      <Box sx={{ mt: 8, mb: 4 }} />
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header
        cart={cart}
        setCartOpen={setCartOpen}
        onNavigate={handleNavigate}
        categories={categorias.map(({ label, value }) => ({ label, value }))}
        onSelectCategory={handleSelectCategory}
      />

      <Routes>
        <Route path="/" element={homeContent} />
        <Route path="/producto/:id" element={<ProductPage addToCart={addToCart} />} />
      </Routes>

      <Box id="contacto" component="div">
        <Footer
          setCategoriaSeleccionada={setCategoriaSeleccionada}
          setMostrarProductos={setMostrarProductos}
          stats={stats}
        />
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={1800} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={Boolean(statDetail)}
        onClose={() => setStatDetail(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box>
            <Typography component="span" variant="h6" fontWeight="bold" color="#388e3c">
              {statDetail?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {statDetail?.items.length ?? 0} en total
            </Typography>
          </Box>
          <IconButton onClick={() => setStatDetail(null)} size="small" aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 2 }, py: 1 }}>
          {!statDetail || statDetail.items.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No hay elementos para mostrar.
            </Typography>
          ) : (
            <List dense disablePadding>
              {statDetail.items.map((item, index) => (
                <ListItem
                  key={`${item}-${index}`}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    bgcolor: index % 2 === 0 ? 'rgba(76, 175, 80, 0.06)' : 'transparent',
                    py: 0.75,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CircleIcon sx={{ fontSize: 8, color: '#66bb6a' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatDetail(null)} color="success">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={cartOpen} onClose={() => setCartOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Carrito de Cotización
          <IconButton onClick={() => setCartOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {cart.length === 0 ? (
            <Typography variant="body1" color="text.secondary">No hay productos en el carrito.</Typography>
          ) : (
            <Box>
              {cart.map((item) => (
                <Box key={item.product.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                    <img src={item.product.image} alt={item.product.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }} />
                    <Typography fontWeight="bold" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.product.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <TextField
                      type="number"
                      size="small"
                      label="Cant."
                      value={item.quantity}
                      onChange={e => updateCartQuantity(item.product.id, Math.max(1, parseInt(e.target.value) || 1))}
                      inputProps={{ min: 1, style: { width: 50, textAlign: 'center' } }}
                      sx={{ mx: 1 }}
                    />
                    <IconButton color="error" size="small" onClick={() => removeFromCart(item.product.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="success" disabled={cart.length === 0} onClick={() => {
            const msg = encodeURIComponent('Hola, quiero cotizar los siguientes productos: ' + cart.map(item => `\n- ${item.product.name} x${item.quantity}`).join(''));
            window.open('https://wa.me/526622055654?text=' + msg, '_blank');
          }}>Cotizar por WhatsApp</Button>
          <Button variant="outlined" color="primary" disabled={cart.length === 0} onClick={() => {
            const subject = encodeURIComponent('Cotización de productos Defender');
            const body = encodeURIComponent('Hola, quiero cotizar los siguientes productos: ' + cart.map(item => `\n- ${item.product.name} x${item.quantity}`).join(''));
            window.open(`mailto:admi.elpoderoso@gmail.com?subject=${subject}&body=${body}`);
          }}>Cotizar por Email</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
